import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import PrimaryButton from '../../../components/PrimaryButton';
import BottomNavigator from '../../../components/BottomNavigator';
import AutoNavigate from '../../../components/AutoNavigate';
import IdleDriversAvailable from '../../../components/IdleDriversAvailable';
import BookingQueueAvailable from '../../../components/BookingQueueAvailable';
import BookingQueueUnavailable from '../../../components/BookingQueueUnavailable';
import ServiceUnavailable from '../../../components/ServiceUnavailable';
import AllDriversBusy from '../../../components/AllDriversBusy';

import { AppStateContext } from '../../../context/AppStateContextProvider';
import { defaultApiClient } from '../../../apiClients/apiClient';

const numIdleDriversUpdateInterval = 5_000;

export default function RideIdlePage() {
    const navigate = useNavigate();

    const { deviceLocation, fetchUserInteractionState } = useContext(AppStateContext);

    const [isBooking, setIsBooking] = useState(false);
    const [rideBookingLogicData, setRideBookingLogicData] = useState(null);
    const minimumFareFirst2Km = rideBookingLogicData?.ridePricing?.minimumFareFirst2Km;
    const farePerKmAfterFirst2Km = rideBookingLogicData?.ridePricing?.farePerKmAfterFirst2Km;
    const freeRidesRemaining = rideBookingLogicData?.freeRidesRemaining;
    const hasFreeRides = typeof freeRidesRemaining === 'number' && freeRidesRemaining > 0;

    const getRideBookingLogicDataFromBackend = async () => {
        const getRideBookingLogicDataResponse =
            await defaultApiClient.methods.rides.customer.getRideBookingLogicData();
        if (!getRideBookingLogicDataResponse.success) {
            return;
        }

        setRideBookingLogicData(getRideBookingLogicDataResponse.result);
        console.log(getRideBookingLogicDataResponse.result);
    };

    const handleBookNowButtonClick = async () => {
        if (isBooking) {
            return;
        }

        if (rideBookingLogicData === null) {
            window.alert('Loading. Please wait ...');
            return;
        }

        if (
            rideBookingLogicData.bookingLogicStatus ===
            'NO_IDLE_DRIVERS_AND_BOOKING_QUEUE_UNAVAILABLE'
        ) {
            window.alert(
                'No Drivers Available: You cannot book a ride at the moment as no drivers are free. Please try again in a few minutes.'
            );
            return;
        }

        if (rideBookingLogicData.bookingLogicStatus === 'SERVICE_UNAVAILABLE') {
            window.alert(
                'No Drivers Available: You cannot book a ride at the moment as no drivers are free. Please try again in a few minutes.'
            );
            return;
        }

        if (rideBookingLogicData.bookingLogicStatus === 'ALL_DRIVERS_BUSY') {
            window.alert(
                'No Drivers Available: You cannot book a ride at the moment as no drivers are free. Please try again in a few minutes.'
            );
            return;
        }

        let initializeOrQueueRideBookingResponse;
        if (rideBookingLogicData.bookingLogicStatus === 'IDLE_DRIVERS_AVAILABLE') {
            setIsBooking(true);
            initializeOrQueueRideBookingResponse =
                await defaultApiClient.methods.rides.customer.initializeRideBooking(deviceLocation);
        } else if (
            rideBookingLogicData.bookingLogicStatus ===
            'NO_IDLE_DRIVERS_BUT_BOOKING_QUEUE_AVAILABLE'
        ) {
            setIsBooking(true);
            initializeOrQueueRideBookingResponse =
                await defaultApiClient.methods.rides.customer.queueRideBooking(deviceLocation);
        } else {
            window.alert(
                'No Drivers Available: You cannot book a ride at the moment as no drivers are free. Please try again in a few minutes.'
            );
            return;
        }

        if (!initializeOrQueueRideBookingResponse.success) {
            console.log(initializeOrQueueRideBookingResponse);
            window.alert(
                `${initializeOrQueueRideBookingResponse.error.userFriendlyError.title}: ${initializeOrQueueRideBookingResponse.error.userFriendlyError.detail}`
            );
            setIsBooking(false);
            return;
        }

        // setIsBooking(false);
        fetchUserInteractionState();
    };

    useEffect(() => {
        const fetchRideBookingLogicDataIntervalId = setInterval(
            getRideBookingLogicDataFromBackend,
            numIdleDriversUpdateInterval
        );
        getRideBookingLogicDataFromBackend();

        return () => {
            clearInterval(fetchRideBookingLogicDataIntervalId);
        };
    }, []);

    return (
        <Box
            sx={{
                width: '100vw',
                height: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'center',
            }}
        >
            <AutoNavigate />
            <Box
                sx={{
                    backgroundImage: `url('/idle-screen-background.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    width: '100vw',
                    height: '100dvh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    position: 'relative',
                    top: -35,
                }}
            >
                {/* Loading */}
                {rideBookingLogicData === null && (
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginTop: 10,
                        }}
                    >
                        <CircularProgress
                            size={34}
                            sx={{
                                color: 'white',
                            }}
                        />
                    </Box>
                )}

                {/* {rideBookingLogicData !== null && <IdleDriversAvailable rideBookingLogicData={rideBookingLogicData} />} */}

                {rideBookingLogicData !== null &&
                    rideBookingLogicData.bookingLogicStatus === 'IDLE_DRIVERS_AVAILABLE' && (
                        <IdleDriversAvailable rideBookingLogicData={rideBookingLogicData} />
                    )}

                {rideBookingLogicData !== null &&
                    rideBookingLogicData.bookingLogicStatus ===
                        'NO_IDLE_DRIVERS_BUT_BOOKING_QUEUE_AVAILABLE' && (
                        <BookingQueueAvailable rideBookingLogicData={rideBookingLogicData} />
                    )}

                {rideBookingLogicData !== null &&
                    rideBookingLogicData.bookingLogicStatus ===
                        'NO_IDLE_DRIVERS_AND_BOOKING_QUEUE_UNAVAILABLE' && (
                        <BookingQueueUnavailable rideBookingLogicData={rideBookingLogicData} />
                    )}

                {rideBookingLogicData !== null &&
                    rideBookingLogicData.bookingLogicStatus === 'SERVICE_UNAVAILABLE' && (
                        <ServiceUnavailable rideBookingLogicData={rideBookingLogicData} />
                    )}

                {rideBookingLogicData !== null &&
                    rideBookingLogicData.bookingLogicStatus === 'ALL_DRIVERS_BUSY' && (
                        <AllDriversBusy rideBookingLogicData={rideBookingLogicData} />
                    )}
            </Box>

            <Box
                sx={{
                    width: '100vw',
                    height: '50dvh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                }}
            >
                {hasFreeRides ? (
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#157347', mb: 2 }}>
                        You have {freeRidesRemaining} Free{' '}
                        {freeRidesRemaining > 1 ? 'Rides' : 'Ride'}
                    </Typography>
                ) : (
                    typeof minimumFareFirst2Km === 'number' &&
                    typeof farePerKmAfterFirst2Km === 'number' && (
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#4a4a4a', mb: 2 }}>
                            ₹{minimumFareFirst2Km} for 2km, then ₹{farePerKmAfterFirst2Km} per km
                        </Typography>
                    )
                )}

                <PrimaryButton
                    text="Book Now"
                    onClick={handleBookNowButtonClick}
                    disabled={
                        isBooking ||
                        rideBookingLogicData === null ||
                        (rideBookingLogicData.bookingLogicStatus !== 'IDLE_DRIVERS_AVAILABLE' &&
                            rideBookingLogicData.bookingLogicStatus !==
                                'NO_IDLE_DRIVERS_BUT_BOOKING_QUEUE_AVAILABLE')
                    }
                    loading={isBooking}
                    disabledTooltip={
                        rideBookingLogicData === null
                            ? 'Loading ... Please Wait.'
                            : rideBookingLogicData.bookingLogicStatus !== 'IDLE_DRIVERS_AVAILABLE'
                            ? 'No Drivers Available: You cannot book a ride at the moment as no drivers are free'
                            : rideBookingLogicData.bookingLogicStatus === 'SERVICE_UNAVAILABLE'
                            ? 'Zipzy is currently unavailable.'
                            : rideBookingLogicData.bookingLogicStatus === 'ALL_DRIVERS_BUSY'
                            ? 'No drivers are available at the moment. Please try again in a few minutes.'
                            : rideBookingLogicData.bookingLogicStatus ===
                              'NO_IDLE_DRIVERS_AND_BOOKING_QUEUE_UNAVAILABLE'
                            ? 'No drivers are available at the moment. Please try again in a few minutes.'
                            : ''
                    }
                />
            </Box>
            <BottomNavigator />
        </Box>
    );
}
